import React from 'react';
import { Box, Button, Typography } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import { Loader, Typography as CommonsTypography, SettingsToggle } from '@orijinworks/frontend-commons';
import { withStyles } from '@material-ui/styles';
import {
  permissionToggleRow,
  permissionToggleTextColumn,
  permissionToggleLabel,
  permissionToggleDescription,
  permissionStepErrorText,
  permissionsProfileTabHeader,
  permissionsProfileTabTitle,
  permissionsProfileTabIntro,
  permissionsProfileEditButton,
  permissionsProfileTabBody,
  permissionsProfileGroupOverline,
  permissionStatusChipOn,
  permissionStatusChipOff,
  cancelButton,
  getCreateButtonStyles,
} from '../../../../v2/modules/create-staff/CreateStaff.styles';
import useStaffPermissionsTabHook from './useStaffPermissionsTabHook';
import staffProfileStyles from './staff-profile.style';

/** Default section heading when the catalog is shown as one block (matches design). */
const DEFAULT_PERMISSION_GROUP_OVERLINE = 'FEEDS';

function permissionSupportingCopy(item) {
  return item?.description ?? '';
}

/**
 * Staff profile Permissions tab: effective role defaults ± overrides from Janus.
 * View: group overline, title + description, ON/OFF chips. Edit: toggles + Cancel / Save.
 */
function StaffPermissionsTab({ personId, hasActiveLocation = true, canEdit, onSaveSuccess, classes }) {
  const {
    items,
    toggleState,
    loading,
    saving,
    error,
    isDirty,
    showEditChrome,
    handleStartEdit,
    handleCancelEdit,
    handleSave,
    handleTogglePermission,
  } = useStaffPermissionsTabHook({ personId, canEdit, onSaveSuccess, hasActiveLocation });

  if (!hasActiveLocation) {
    return (
      <Box width="100%">
        <Box className={classes.facilitiesSectionHeader}>
          <Box>
            <Typography component="h2" className={classes.facilitiesHeading}>
              Permissions Unavailable
            </Typography>
            <Typography className={classes.facilitiesExplainer}>
              This user account is currently inactive. You must reactivate the user to view or modify their
              permissions.
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <Loader isLoading />
      </Box>
    );
  }

  if (error && items.length === 0) {
    return (
      <Typography style={permissionStepErrorText} component="p">
        {error}
      </Typography>
    );
  }

  return (
    <Box width="100%">
      <Box style={permissionsProfileTabHeader}>
        <Box flex={1} minWidth={0}>
          <Typography component="h2" style={permissionsProfileTabTitle}>
            Permissions
          </Typography>
          <Typography component="p" style={permissionsProfileTabIntro}>
            The user will have these permissions across all facilities where these features are enabled.
          </Typography>
        </Box>
        {canEdit && !showEditChrome ? (
          <Button
            variant="outlined"
            color="primary"
            style={permissionsProfileEditButton}
            startIcon={<EditIcon style={{ fontSize: 16, color: 'var(--primary-blue-primary-main, #0F48B8)' }} />}
            onClick={handleStartEdit}
          >
            Edit Permissions
          </Button>
        ) : null}
      </Box>

      <Box style={permissionsProfileTabBody}>
        {error ? (
          <Typography style={permissionStepErrorText} component="p">
            {error}
          </Typography>
        ) : null}

        <Box width="100%">
          <Typography component="h3" style={permissionsProfileGroupOverline}>
            {DEFAULT_PERMISSION_GROUP_OVERLINE}
          </Typography>

          {items.map((item) => {
            const title = item.permissionDisplayName || item.permissionName;
            const body = permissionSupportingCopy(item);
            const enabled = Boolean(toggleState[item.permissionId]);

            if (showEditChrome) {
              return (
                <Box key={item.permissionId} style={permissionToggleRow} component="div">
                  <Box style={permissionToggleTextColumn}>
                    <CommonsTypography variant="body1" component="div" sx={permissionToggleLabel}>
                      {title}
                    </CommonsTypography>
                    {body ? (
                      <CommonsTypography variant="body2" component="div" sx={permissionToggleDescription}>
                        {body}
                      </CommonsTypography>
                    ) : null}
                  </Box>
                  <SettingsToggle
                    checked={enabled}
                    onChange={(next) => handleTogglePermission(item.permissionId, next)}
                  />
                </Box>
              );
            }

            return (
              <Box key={item.permissionId} style={permissionToggleRow} component="div">
                <Box style={permissionToggleTextColumn}>
                  <CommonsTypography variant="body1" component="div" sx={permissionToggleLabel}>
                    {title}
                  </CommonsTypography>
                  {body ? (
                    <CommonsTypography variant="body2" component="div" sx={permissionToggleDescription}>
                      {body}
                    </CommonsTypography>
                  ) : null}
                </Box>
                <Box
                  component="span"
                  style={enabled ? permissionStatusChipOn : permissionStatusChipOff}
                  aria-label={enabled ? 'On' : 'Off'}
                >
                  {enabled ? 'ON' : 'OFF'}
                </Box>
              </Box>
            );
          })}
        </Box>

        {showEditChrome ? (
          <Box display="flex" justifyContent="flex-end" alignItems="center" flexWrap="wrap">
            <Box mr={1.5} display="inline-block">
              <Button variant="outlined" style={cancelButton} onClick={handleCancelEdit} disabled={saving}>
                Cancel
              </Button>
            </Box>
            <Button
              variant="contained"
              style={getCreateButtonStyles(!isDirty || saving)}
              onClick={handleSave}
              disabled={!isDirty || saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

export default withStyles(staffProfileStyles)(StaffPermissionsTab);
